import React, { useState, useEffect, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		backgroundColor: theme.palette.background.default,
		borderTop: `1px solid ${theme.palette.divider}`,
		borderRight: `1px solid ${theme.palette.divider}`,
		borderBottom: `1px solid ${theme.palette.divider}`,
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		alignItems: "center",
		padding: theme.spacing(1, 1),
		minHeight: 64,
		justifyContent: "flex-start",
	},
	headerTitle: {
		fontWeight: 600,
		color: theme.palette.text.primary,
	},
	closeButton: {
		color: theme.palette.text.secondary,
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.background.default,
		flexDirection: "column",
		padding: theme.spacing(1.5),
		gap: theme.spacing(1.5),
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	profileCard: {
		padding: theme.spacing(2.5, 2),
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		borderRadius: 10,
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
	},
	contactHeader: {
		padding: theme.spacing(2.5, 2),
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		borderRadius: 10,
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
	},

	contactAvatar: {
		marginBottom: theme.spacing(1.5),
		width: 124,
		height: 124,
		border: `2px solid ${theme.palette.background.default}`,
	},
	contactName: {
		fontWeight: 600,
		color: theme.palette.text.primary,
		textAlign: "center",
	},
	contactNumber: {
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(1.5),
	},
	phoneLink: {
		fontWeight: 500,
		fontSize: "0.95rem",
		color: theme.palette.primary.main,
		"&:hover": {
			color: theme.palette.primary.light,
			textDecorationColor: theme.palette.primary.light,
		},
	},
	editButton: {
		minWidth: 140,
	},

	contactDetails: {
		padding: theme.spacing(1.5),
		display: "flex",
		flexDirection: "column",
		borderRadius: 10,
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
	},
	sectionTitle: {
		fontWeight: 600,
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(1),
	},
	contactExtraInfo: {
		marginTop: theme.spacing(1),
		padding: theme.spacing(1.25),
		borderRadius: 8,
		backgroundColor: theme.palette.background.default,
		border: `1px solid ${theme.palette.divider}`,
	},
	infoLabel: {
		fontSize: "0.76rem",
		textTransform: "uppercase",
		letterSpacing: "0.04em",
		fontWeight: 700,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5),
	},
	infoValue: {
		color: theme.palette.text.primary,
		lineHeight: 1.4,
	},
	emptyState: {
		color: theme.palette.text.secondary,
		fontSize: "0.85rem",
		fontStyle: "italic",
	},
	hubspotActions: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	btnPull: {
		color: theme.palette.text.secondary,
		borderColor: theme.palette.divider,
		"&:hover": {
			borderColor: theme.palette.primary.main,
			color: theme.palette.primary.main,
			backgroundColor: "transparent",
		},
	},
	btnSaveHubspot: {
		backgroundColor: "#ff7a59",
		color: "#fff",
		"&:hover": {
			backgroundColor: "#e8623f",
		},
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, loading, onContactUpdated, connectionName, assignedUserName }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [hubspotModalOpen, setHubspotModalOpen] = useState(false);
	const [pulling, setPulling] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: "" });
	const autoPulledRef = useRef(null);

	// Auto-pull from HubSpot when drawer opens for a contact that has hubspotId but no local data
	useEffect(() => {
		if (!open || !contact?.id || !contact?.hubspotId) return;
		if (contact.lastName || contact.email || contact.country) return;
		if (autoPulledRef.current === contact.id) return;
		autoPulledRef.current = contact.id;
		api.post(`/hubspot/pull/${contact.id}`).catch(() => {});
	}, [open, contact?.id, contact?.hubspotId]);

	const handlePullFromHubSpot = async () => {
		if (!contact?.id) return;
		setPulling(true);
		try {
			const { data } = await api.post(`/hubspot/pull/${contact.id}`);
			if (data.updated) {
				setSnackbar({ open: true, message: i18n.t("contactDrawer.pullHubSpot.success") });
				if (onContactUpdated) onContactUpdated();
			} else {
				setSnackbar({ open: true, message: i18n.t("contactDrawer.pullHubSpot.noData") });
			}
		} catch {
			setSnackbar({ open: true, message: i18n.t("contactDrawer.pullHubSpot.error") });
		} finally {
			setPulling(false);
		}
	};

	return (
		<>
			<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
				disableScrollLock: true,
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton className={classes.closeButton} onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography className={classes.headerTitle}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper className={classes.profileCard}>
						<Avatar
							alt={contact.name}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography variant="h6" className={classes.contactName}>
							{contact.name}
						</Typography>
						<Typography className={classes.contactNumber}>
							<Link className={classes.phoneLink} href={`tel:${contact.number}`}>
								{contact.number}
							</Link>
						</Typography>
						<Button
							className={classes.editButton}
							variant="outlined"
							color="primary"
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>
					<Paper className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						<ContactModal
							open={hubspotModalOpen}
							onClose={() => setHubspotModalOpen(false)}
							contactId={contact.id}
							saveToHubSpot={true}
							cluster={connectionName}
							ownerName={assignedUserName}
						></ContactModal>
						<Typography variant="subtitle1" className={classes.sectionTitle}>
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{contact?.email && (
							<Paper className={classes.contactExtraInfo}>
								<InputLabel className={classes.infoLabel}>Email</InputLabel>
								<Typography className={classes.infoValue}>{contact.email}</Typography>
							</Paper>
						)}
						{contact?.lastName && (
							<Paper className={classes.contactExtraInfo}>
								<InputLabel className={classes.infoLabel}>{i18n.t("contactModal.form.lastName")}</InputLabel>
								<Typography className={classes.infoValue}>{contact.lastName}</Typography>
							</Paper>
						)}
						{contact?.age && (
							<Paper className={classes.contactExtraInfo}>
								<InputLabel className={classes.infoLabel}>{i18n.t("contactModal.form.age")}</InputLabel>
								<Typography className={classes.infoValue}>{contact.age}</Typography>
							</Paper>
						)}
						{contact?.country && (
							<Paper className={classes.contactExtraInfo}>
								<InputLabel className={classes.infoLabel}>{i18n.t("contactModal.form.country")}</InputLabel>
								<Typography className={classes.infoValue}>{contact.country}</Typography>
							</Paper>
						)}
						{contact?.description && (
							<Paper className={classes.contactExtraInfo}>
								<InputLabel className={classes.infoLabel}>{i18n.t("contactModal.form.description")}</InputLabel>
								<Typography className={classes.infoValue} component="div">
									<MarkdownWrapper>{contact.description}</MarkdownWrapper>
								</Typography>
							</Paper>
						)}
						{!contact?.extraInfo?.length && !contact?.email && !contact?.lastName && !contact?.age && !contact?.country && !contact?.description && (
							<Typography className={classes.emptyState}>-</Typography>
						)}
						{contact?.extraInfo?.map(info => (
							<Paper
								key={info.id}
								className={classes.contactExtraInfo}
							>
								<InputLabel className={classes.infoLabel}>{info.name}</InputLabel>
								<Typography className={classes.infoValue} component="div">
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
						</Paper>
						<div className={classes.hubspotActions}>
							<Button
								variant="outlined"
								fullWidth
								className={classes.btnPull}
								onClick={handlePullFromHubSpot}
								disabled={pulling}
								startIcon={pulling ? <CircularProgress size={16} /> : null}
							>
								{i18n.t("contactDrawer.buttons.pullFromHubSpot")}
							</Button>
							<Button
								variant="contained"
								fullWidth
								className={classes.btnSaveHubspot}
								onClick={() => setHubspotModalOpen(true)}
							>
								{i18n.t("contactDrawer.buttons.saveToHubSpot")}
							</Button>
						</div>
					</div>
				)}
			</Drawer>
			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar(s => ({ ...s, open: false }))}
				message={snackbar.message}
			/>
		</>
	);
};

export default ContactDrawer;
